import moment from "moment-timezone";

export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_error) {
    return "UTC";
  }
};

// The class API stores UTC. A datetime-local value has no offset, so interpret
// it in the salon's timezone before serialising it to an ISO instant.
export const toIsoInstant = (localValue, timezone) => {
  if (!localValue) return "";
  if (timezone && moment.tz.zone(timezone)) {
    const parsed = moment.tz(localValue, "YYYY-MM-DDTHH:mm", true, timezone);
    return parsed.isValid() ? parsed.toDate().toISOString() : "";
  }
  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString();
};

export const formatSessionStart = (isoValue, timezone) => {
  if (!isoValue) return "";
  if (timezone && moment.tz.zone(timezone)) {
    const parsed = moment.utc(isoValue);
    return parsed.isValid() ? parsed.tz(timezone).format("MMM DD, YYYY hh:mm A") : "";
  }
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) return "";
  return moment(parsed).format("MMM DD, YYYY hh:mm A");
};

export const remainingLabel = (session) => {
  const capacity = Number(session && session.capacity ? session.capacity : 0);
  const remaining = Number(session && session.remaining_slots ? session.remaining_slots : 0);
  return `${remaining} of ${capacity} slots free`;
};

// Staff options arrive from a Laravel-shaped query, so accept the shapes the
// rest of the app produces instead of assuming one of them.
export const extractStaffOptions = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : payload && Array.isArray(payload.data)
      ? payload.data
      : [];
  return rows
    .map((row) => {
      if (Array.isArray(row) && row.length >= 2) {
        return { value: row[0], label: String(row[1]) };
      }
      if (row && typeof row === "object") {
        const value = row.id !== undefined ? row.id : row.value;
        const label =
          row.label !== undefined
            ? row.label
            : [row.first_name, row.last_name].filter(Boolean).join(" ");
        if (value !== undefined && value !== null && value !== "") {
          return { value, label: String(label || value) };
        }
      }
      return null;
    })
    .filter(Boolean);
};

export const extractResourceItemOptions = (payload) => {
  const resources = Array.isArray(payload)
    ? payload
    : payload && Array.isArray(payload.data)
      ? payload.data
      : [];
  const seen = new Set();
  return resources.flatMap((resource) => {
    const items = Array.isArray(resource?.resource_items)
      ? resource.resource_items
      : Array.isArray(resource?.resourceItems)
        ? resource.resourceItems
        : [];
    return items.flatMap((item) => {
      const value = item?.id ?? item?.value;
      const itemName = typeof item?.resource_item === "string"
        ? item.resource_item
        : item?.resource_item?.name ?? item?.resource_item?.label ?? item?.name ?? item?.label;
      if (value == null || value === "" || !itemName || seen.has(String(value))) return [];
      seen.add(String(value));
      const resourceName = resource?.resource_name ?? resource?.name;
      return [{
        value,
        label: resourceName ? `${resourceName} — ${itemName}` : String(itemName),
      }];
    });
  });
};

export const buildCreatePayload = (form) => ({
  name: form.name,
  start_at: toIsoInstant(form.start_at, form.timezone),
  duration_minutes: Number(form.duration_minutes) || 0,
  capacity: Number(form.capacity) || 0,
  timezone: form.timezone || browserTimezone(),
  price: form.price === "" || form.price === undefined ? "0.00" : String(form.price),
  deposit_amount:
    form.deposit_amount === "" || form.deposit_amount === undefined
      ? "0.00"
      : String(form.deposit_amount),
  cancellation_window_hours: Number(form.cancellation_window_hours) || 0,
  staff_id: form.staff_id ? Number(form.staff_id) : null,
  resource_item_id: form.resource_item_id ? Number(form.resource_item_id) : null,
  is_online_bookable: form.is_online_bookable !== false,
});
